import { io, Socket } from 'socket.io-client';
import { store } from 'Store/Store';
// import API from 'Shared/connectors/Config';

let socket: Socket | null = null;

function getApiUrl(): string {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3333';
  return apiUrl.replace(/\/$/, '');
}

async function getAuthToken(): Promise<string | null> {
  const token = store.getState().authStore.token;
  if (token) {
    return token.startsWith('Bearer ') ? token.replace('Bearer ', '') : token;
  }

  // Backend doesn't have an auth/refresh endpoint, so if token is missing
  // in Redux we'll rely entirely on the HTTP-Only cookie being sent automatically
  // (which is handled by withCredentials: true on the socket connection)

  return null;
}

export async function connect(onConnect?: () => void, onDisconnect?: () => void): Promise<Socket | null> {
  if (socket?.connected) return socket;

  const token = await getAuthToken();
  if (token) {
    console.log('[AiChat] Using token from store/refresh');
  } else {
    console.log('[AiChat] No token in store, relying on HTTP-Only cookie');
  }

  const url = getApiUrl();

  socket = io(url, {
    path: '/ws/',
    auth: { token },
    withCredentials: true,
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });

  socket.on('connect', () => {
    console.log('[AiChat] Connected to WebSocket');
    onConnect?.();
  });

  socket.on('disconnect', (reason) => {
    console.log('[AiChat] Disconnected:', reason);
    onDisconnect?.();
  });

  socket.on('connect_error', (error) => {
    console.error('[AiChat] Connection error:', error.message);
  });

  return socket;
}

/**
 * Disconnect from the WebSocket server
 */
export function disconnect(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

/**
 * Get the socket instance (must be connected first)
 */
export function getSocket(): Socket | null {
  return socket;
}

/**
 * List all user's chats
 */
export function listChats(): Promise<any> {
  return new Promise((resolve, reject) => {
    if (!socket) return reject(new Error('Not connected'));
    socket.emit('chat:list', (response: any) => {
      if (response.success) {
        resolve(response.data);
      } else {
        reject(new Error(response.error));
      }
    });
  });
}

/**
 * Create a new chat
 */
export function createChat(): Promise<any> {
  return new Promise((resolve, reject) => {
    if (!socket) return reject(new Error('Not connected'));
    socket.emit('chat:create', (response: any) => {
      if (response.success) {
        resolve(response.data);
      } else {
        reject(new Error(response.error));
      }
    });
  });
}

/**
 * Load chat message history
 */
export function getChatHistory(chatUuid: string): Promise<any> {
  return new Promise((resolve, reject) => {
    if (!socket) return reject(new Error('Not connected'));
    socket.emit('chat:history', { chatUuid }, (response: any) => {
      if (response.success) {
        resolve(response.data);
      } else {
        reject(new Error(response.error));
      }
    });
  });
}

/**
 * Send a message to a chat
 */
export function sendMessage(chatUuid: string, content: string): void {
  if (!socket) return;
  socket.emit('chat:message', { chatUuid, content });
}

/**
 * Delete a chat
 */
export function deleteChat(chatUuid: string): Promise<any> {
  return new Promise((resolve, reject) => {
    if (!socket) return reject(new Error('Not connected'));
    socket.emit('chat:delete', { chatUuid }, (response: any) => {
      if (response.success) {
        resolve(true);
      } else {
        reject(new Error(response.error));
      }
    });
  });
}

/**
 * Register event listeners for streaming
 */
export function onStream(handler: (data: { chatUuid: string; chunk: string }) => void): void {
  socket?.on('chat:stream', handler);
}

export function onStreamEnd(handler: (data: { chatUuid: string; message: any }) => void): void {
  socket?.on('chat:stream:end', handler);
}

export function onStreamError(handler: (data: { chatUuid: string; error: string }) => void): void {
  socket?.on('chat:stream:error', handler);
}

export function onMessageSaved(handler: (data: { chatUuid: string; message: any }) => void): void {
  socket?.on('chat:message:saved', handler);
}

export function onTitleUpdated(handler: (data: { chatUuid: string; title: string }) => void): void {
  socket?.on('chat:title:updated', handler);
}

/**
 * Remove all event listeners (for cleanup)
 */
export function removeAllListeners(): void {
  socket?.off('chat:stream');
  socket?.off('chat:stream:end');
  socket?.off('chat:stream:error');
  socket?.off('chat:message:saved');
  socket?.off('chat:title:updated');
}
